<script>
  import { External, M } from '../../../external';
  import { onDestroy, onMount } from 'svelte';
  import ActivityEntry from './Entry/ActivityEntry.svelte';
  import NoDataPlaceholder from '../../../Common/NoDataPlaceholder.svelte';

  export let contact, container;

  let endOfEntries;
  let observer;

  const activityTabs = {
    currentTab: 'all',
    changeTab: (tab) => {
      activityTabs.currentTab = tab;
      activity.reset();
      fetchUntilFull(tab);
    },
    tabs: {
      all: {
        title: M.ALL,
        isVisible: () => true
      },
      emails: {
        title: M.EMAILS,
        isVisible: () => true
      },
      messages: {
        title: M.MESSAGES,
        isVisible: () => true
      },
      orders: {
        title: M.ORDERS,
        isVisible: () => true
      },
      appointments: {
        title: M.APPOINTMENTS,
        isVisible: () => true
      },
      forms: {
        title: M.FORMS,
        isVisible: () => true
      },
      videos: {
        title: M.VIDEOS,
        isVisible: () => true
      },
      other: {
        title: M.OTHER,
        isVisible: () => true
      }
    }
  };

  const createInitialPaginationState = () => ({
    before: new Date().toISOString(),
    exhausted: false
  });

  const activity = {
    loading: false,
    data: [],
    activityServiceState: createInitialPaginationState(),
    historyServiceState: createInitialPaginationState(),
    activityBuffer: [],
    historyBuffer: [],
    displayBefore: null,
    limit: 20,
    dontFetchAnymore: false,
    reset: () => {
      activity.loading = false;
      activity.data = [];
      activity.activityServiceState = createInitialPaginationState();
      activity.historyServiceState = createInitialPaginationState();
      activity.activityBuffer = [];
      activity.historyBuffer = [];
      activity.displayBefore = null;
      activity.dontFetchAnymore = false;
    }
  };

  const shouldFetchFromActivityService = (category) => category !== 'emails';
  const shouldFetchFromHistoryService = (category) => category === 'all' || category === 'emails';

  const buildActivityUrl = (category) => {
    let url = `https://${External.serviceApiDomain}/activity/${contact.data.id}`;
    if (category !== 'all') {
      url += `/${category}`;
    }
    url += `?before=${activity.activityServiceState.before}&limit=${activity.limit}`;
    return url;
  };

  const fetchFromActivityService = async (category) => {
    const response = await fetch(buildActivityUrl(category), {
      method: 'GET',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.Website.hash
      }
    });
    return await response.json();
  };

  const fetchFromHistoryService = () => {
    return new Promise((resolve) => {
      External.Tools.makeWebsiteRequest({
        action: 'marketingCampaignHistory',
        data: {
          email: contact.data.email,
          before: activity.historyServiceState.before,
          limit: activity.limit
        },
        onOk: (response) => {
          if (response?.data) {
            resolve(convertCampaignHistoryToActivityEvents(response.data));
          } else {
            resolve([]);
          }
        }
      });
    });
  };

  const convertCampaignHistoryToActivityEvents = (history) => {
    const ACTIVITY_TYPES = { send: 'EMAIL_SENT', open: 'EMAIL_OPENED', click: 'EMAIL_CLICKED' };

    return history.map((entry) => ({
      timestamp: entry.timestamp,
      activityType: ACTIVITY_TYPES[entry.event],
      data: { email: entry },
      actionMaker: { isAdmin: entry.event === 'send' }
    }));
  };

  const advanceCursor = (state, items) => {
    if (items.length > 0) {
      const oldest = items[items.length - 1];
      state.before = oldest.timestamp;
    }
  };

  const addToBuffer = (buffer, items) => {
    if (items.length > 0) {
      return [...buffer, ...items];
    }
    return buffer;
  };

  const markExhaustedIfNeeded = (state, didFetch, responseLength, limit) => {
    if (didFetch && responseLength < limit) {
      state.exhausted = true;
    }
  };

  const createHistoryEligibilityFilter = (category) => {
    const isActivityGateEnabled = shouldFetchFromActivityService(category);

    return (item) => {
      // If activity source not used for this category, allow all history
      if (!isActivityGateEnabled) return true;
      // If activity source is exhausted, flush all deferred history
      if (activity.activityServiceState.exhausted) return true;
      // Only allow history items within the activity timeline range
      return activity.activityServiceState.before <= item.timestamp;
    };
  };

  const mergeAndTag = (activityItems, historyItems) => {
    const tagged = [
      ...activityItems.map((item) => ({ item, source: 'activity' })),
      ...historyItems.map((item) => ({ item, source: 'history' }))
    ];
    return tagged.sort((a, b) => b.item.timestamp.localeCompare(a.item.timestamp));
  };

  const appendToTimeline = (mergedItems) => {
    if (mergedItems.length === 0) return;

    activity.data = [...activity.data, ...mergedItems.map((entry) => entry.item)];
    activity.displayBefore = mergedItems[mergedItems.length - 1].item.timestamp;
  };

  const removeConsumedFromBuffers = (mergedItems) => {
    const consumedActivity = new Set(mergedItems.filter((e) => e.source === 'activity').map((e) => e.item));
    const consumedHistory = new Set(mergedItems.filter((e) => e.source === 'history').map((e) => e.item));

    activity.activityBuffer = activity.activityBuffer.filter((item) => !consumedActivity.has(item));
    activity.historyBuffer = activity.historyBuffer.filter((item) => !consumedHistory.has(item));
  };

  const fetchActivities = async (category) => {
    activity.loading = true;

    // Determine what to fetch
    const shouldFetchActivity = shouldFetchFromActivityService(category) && !activity.activityServiceState.exhausted;
    const shouldFetchHistory = shouldFetchFromHistoryService(category) && !activity.historyServiceState.exhausted;

    // Fetch from sources
    const activityResponse = shouldFetchActivity ? await fetchFromActivityService(category) : [];
    const historyResponse = shouldFetchHistory ? await fetchFromHistoryService() : [];

    // Advance cursors based on fetched data
    advanceCursor(activity.activityServiceState, activityResponse);
    advanceCursor(activity.historyServiceState, historyResponse);

    // Add fetched items to buffers
    activity.activityBuffer = addToBuffer(activity.activityBuffer, activityResponse);
    activity.historyBuffer = addToBuffer(activity.historyBuffer, historyResponse);

    // Mark sources as exhausted
    markExhaustedIfNeeded(activity.activityServiceState, shouldFetchActivity, activityResponse.length, activity.limit);
    markExhaustedIfNeeded(activity.historyServiceState, shouldFetchHistory, historyResponse.length, activity.limit);

    // Filter eligible items from buffers
    const isHistoryEligible = createHistoryEligibilityFilter(category);
    const eligibleActivity = activity.activityBuffer;
    const eligibleHistory = activity.historyBuffer.filter(isHistoryEligible);

    // Merge, sort, and take page-size items
    const mergedItems = mergeAndTag(eligibleActivity, eligibleHistory).slice(0, activity.limit);

    // Append to timeline
    appendToTimeline(mergedItems);

    // Remove consumed items from buffers
    removeConsumedFromBuffers(mergedItems);

    // Update fetch status
    const activityDone = !shouldFetchFromActivityService(category) || activity.activityServiceState.exhausted;
    const historyDone = !shouldFetchFromHistoryService(category) || activity.historyServiceState.exhausted;
    const buffersEmpty = activity.activityBuffer.length === 0 && activity.historyBuffer.length === 0;

    activity.dontFetchAnymore = activityDone && historyDone && buffersEmpty;
    activity.loading = false;
  };

  const isEndOfEntriesAvailable = () => {
    if (!endOfEntries) return false;

    const root = container || document.documentElement;
    const rootRect = root.getBoundingClientRect();
    const rect = endOfEntries.getBoundingClientRect();

    // Check if sentinel is within the visible area (with 100px margin)
    return rect.top < rootRect.bottom + 100;
  };

  const fetchUntilFull = async (category) => {
    while (isEndOfEntriesAvailable() && !activity.dontFetchAnymore && !activity.loading) {
      await fetchActivities(category);
    }
  };

  const setupObserver = () => {
    if (observer) observer.disconnect();

    observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !activity.loading && !activity.dontFetchAnymore) {
          fetchUntilFull(activityTabs.currentTab);
        }
      },
      {
        root: container || null,
        rootMargin: '100px',
        threshold: 0
      }
    );

    if (endOfEntries) {
      observer.observe(endOfEntries);
    }
  };

  // Re-setup observer when container or element changes
  $: if (endOfEntries || container) {
    setupObserver();
  }

  onMount(() => {
    fetchUntilFull(activityTabs.currentTab);
  });

  onDestroy(() => {
    if (observer) observer.disconnect();
  });
</script>

<div class="upper-navigation upper-navigation-full col20 f-left mb-15 flex justify-center">
  <ul style="border-radius: 3px;">
    {#each Object.entries(activityTabs.tabs) as [key, tab]}
      {#if tab.isVisible()}
        <li class="upper-navigation-item">
          <a class:active={activityTabs.currentTab === key} on:click={() => activityTabs.changeTab(key)}>{tab.title}</a>
        </li>
      {/if}
    {/each}
  </ul>
</div>
{#if activity.data.length === 0 && activity.loading}
  <div class="small-centered-loading-icon" style="top:100px;"></div>
{/if}
<!--
<div class="col20 f-left mb-10 manual-activity-wrapper t-center">
  <a class="inline-block small-button with-icon blue rounded-effect">Add Activity</a>
  <a class="circle-button rounded-effect blue flex align-center justify-center f-right">
    <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#5f6368"><path d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z"/></svg>
  </a>
</div>
-->
{#if activity.data.length === 0 && !activity.loading}
  <NoDataPlaceholder />
{/if}
{#each activity.data as entry}
  <ActivityEntry contact={contact.data} {entry} />
{/each}
<div bind:this={endOfEntries} class="communication-row col20 f-left"></div>
